import React, { useCallback, useState } from "react";
import { notBlank } from "../util/ext";
import { lensPath, mergeRight, set } from "ramda";
import Grid from "@mui/material/Grid";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useUserCreate } from "./userServices";
import TextField from "@mui/material/TextField";
import OrganizationAutoComplete from "./OrganizationAutoComplete";
import { validateNewUser, validateNewUserField } from "./userValidation";
import { useErrorBox } from "../manage-organizations/edit/use-errorBox";
import DialogContentText from "@mui/material/DialogContentText";

const CreateUserDialog = ({ open, organizationId, onCancel, onSave }) => {
  const [fields, setFields] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    emailAddress: null,
    firstName: "",
    lastName: "",
    organizationId: organizationId,
  });

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const handleTextChange = path => event => {
    const value = notBlank(event.target) ? event.target.value : event;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleBlur = (field, value) => {
    const validationErrors = validateNewUserField(field, value);
    if (notBlank(validationErrors)) {
      addValidationError(validationErrors);
    } else {
      removeValidationError(field);
    }
  };

  const createUser = useUserCreate();
  const handleApply = useCallback(async () => {
    const validationErrors = validateNewUser(fields);
    console.log(validationErrors);
    const passwordCheck =
      fields.password !== fields.confirmPassword
        ? { confirmPassword: ["Password and Confirm do not match."] }
        : {};
    const allErrors = mergeRight(validationErrors, passwordCheck);

    if (notBlank(allErrors)) {
      setValidationErrors(allErrors);
    } else {
      try {
        const newUser = await createUser.mutateAsync({
          data: fields,
        });
        onSave(newUser.userUuid);
      } catch (err) {
        setGeneralError(err);
      }
    }
  }, [fields, createUser, setGeneralError, setValidationErrors, onSave]);

  return (
    <Dialog open={open} aria-labelledby="new-user-dialog-title">
      <DialogTitle id="add-user-roles-dialog-title">New User</DialogTitle>
      <DialogContent>
        <DialogContentText>Enter new User details.</DialogContentText>
        <DialogErrors />
        <Grid spacing={6} container>
          <Grid item md={6}>
            <TextField
              label="Username"
              fullWidth
              value={fields.username}
              onChange={handleTextChange(["username"])}
              error={hasValidationError("username")}
              helperText={firstValidationError("username")}
              onBlur={() => handleBlur("username", fields.username)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Email Address"
              fullWidth
              value={fields.emailAddress || ""}
              onChange={handleTextChange(["emailAddress"])}
              error={hasValidationError("emailAddress")}
              helperText={firstValidationError("emailAddress")}
              onBlur={() => handleBlur("emailAddress", fields.emailAddress)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Password"
              fullWidth
              type="password"
              value={fields.password}
              onChange={handleTextChange(["password"])}
              error={hasValidationError("password")}
              helperText={firstValidationError("password")}
              onBlur={() => handleBlur("password", fields.password)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Confirm Password"
              fullWidth
              type="password"
              value={fields.confirmPassword}
              onChange={handleTextChange(["confirmPassword"])}
              error={hasValidationError("confirmPassword")}
              helperText={firstValidationError("confirmPassword")}
              onBlur={() =>
                handleBlur("confirmPassword", fields.confirmPassword)
              }
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="First Name (Optional)"
              fullWidth
              value={fields.firstName}
              onChange={handleTextChange(["firstName"])}
              error={hasValidationError("firstName")}
              helperText={firstValidationError("firstName")}
              onBlur={() => handleBlur("firstName", fields.firstName)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Last Name (Optional)"
              fullWidth
              value={fields.lastName}
              onChange={handleTextChange(["lastName"])}
              error={hasValidationError("lastName")}
              helperText={firstValidationError("lastName")}
              onBlur={() => handleBlur("lastName", fields.lastName)}
            />
          </Grid>

          <Grid item xs={12}>
            <OrganizationAutoComplete
              label="Organization"
              value={fields.organizationId}
              filterFunction={org => org.userCreationAllowed && org.active}
              onChange={handleTextChange(["organizationId"])}
              onBlur={() => handleBlur("organizationId", fields.organizationId)}
              error={hasValidationError("organizationId")}
              helperText={firstValidationError("organizationId")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
